import React, { useState, useEffect, useCallback } from 'react';
import {
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

interface WarningLevel {
  id: string;
  threshold: number; // minutes before timeout
  type: 'subtle' | 'prominent' | 'modal';
  severity: 'info' | 'warning' | 'error';
}

const WARNING_LEVELS: WarningLevel[] = [
  {
    id: 'modal',
    threshold: 2, // 2 minutes before
    type: 'modal',
    severity: 'error',
  },
  {
    id: 'prominent',
    threshold: 5, // 5 minutes before
    type: 'prominent',
    severity: 'warning',
  },
  {
    id: 'subtle',
    threshold: 10, // 10 minutes before
    type: 'subtle',
    severity: 'info',
  },
];

export const ProgressiveSessionWarnings: React.FC = () => {
  const {
    isAuthenticated,
    timeUntilIdleTimeout,
    timeUntilAbsoluteTimeout,
    extendSession,
    isSessionIdle,
  } = useAuth();

  const [activeWarning, setActiveWarning] = useState<WarningLevel | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [dismissedWarnings, setDismissedWarnings] = useState<Set<string>>(new Set());

  // Calculate the earliest timeout (either idle or absolute)
  const timeUntilTimeout = Math.min(timeUntilIdleTimeout, timeUntilAbsoluteTimeout);
  const timeoutType = timeUntilIdleTimeout <= timeUntilAbsoluteTimeout ? 'idle' : 'absolute';

  const formatTime = useCallback((ms: number): string => {
    const minutes = Math.floor(ms / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }, []);

  const getWarningMessage = useCallback(
    (warning: WarningLevel, timeLeft: number): string => {
      const timeLeftFormatted = formatTime(timeLeft);
      const sessionType = timeoutType === 'idle' ? 'due to inactivity' : 'session limit reached';

      switch (warning.id) {
        case 'subtle':
          return `Your session will expire in ${timeLeftFormatted} ${sessionType}.`;
        case 'prominent':
          return `Your session will expire in ${timeLeftFormatted} ${sessionType}. Click "Extend Session" to continue.`;
        case 'modal':
          return `Your session will expire in ${timeLeftFormatted} ${sessionType}. Please take action to continue.`;
        default:
          return `Your session will expire in ${timeLeftFormatted}.`;
      }
    },
    [formatTime, timeoutType]
  );

  const handleExtendSession = useCallback(() => {
    extendSession();
    setSnackbarOpen(false);
    setModalOpen(false);
    setActiveWarning(null);
    setDismissedWarnings(new Set()); // Reset dismissed warnings
  }, [extendSession]);

  const handleDismissSnackbar = useCallback((warningId: string) => {
    setSnackbarOpen(false);
    setDismissedWarnings(prev => new Set([...prev, warningId]));
  }, []);

  const handleCloseModal = useCallback(() => {
    // Modal cannot be dismissed without action during critical timeout
    if (activeWarning?.id === 'modal' && timeUntilTimeout > 30000) {
      // Allow close if more than 30s left
      setModalOpen(false);
      setActiveWarning(null);
    }
  }, [activeWarning, timeUntilTimeout]);

  // Main warning logic
  useEffect(() => {
    if (!isAuthenticated || isSessionIdle) {
      setActiveWarning(null);
      setSnackbarOpen(false);
      setModalOpen(false);
      return;
    }

    const timeLeftMinutes = timeUntilTimeout / (1000 * 60);

    // Find the appropriate warning level
    const currentWarning = WARNING_LEVELS.find(
      warning => timeLeftMinutes <= warning.threshold && timeLeftMinutes > 0
    );

    if (!currentWarning) {
      setActiveWarning(null);
      setSnackbarOpen(false);
      setModalOpen(false);
      return;
    }

    // Don't show warnings that have been dismissed (except modal)
    if (currentWarning.type !== 'modal' && dismissedWarnings.has(currentWarning.id)) {
      return;
    }

    // Update active warning
    setActiveWarning(currentWarning);

    // Show appropriate UI
    if (currentWarning.type === 'modal') {
      setSnackbarOpen(false);
      setModalOpen(true);
      setCountdown(timeUntilTimeout);
    } else {
      setModalOpen(false);
      setSnackbarOpen(true);
    }
  }, [isAuthenticated, isSessionIdle, timeUntilTimeout, dismissedWarnings]);

  // Update countdown for modal
  useEffect(() => {
    if (modalOpen && activeWarning?.type === 'modal') {
      const interval = setInterval(() => {
        setCountdown(timeUntilTimeout);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [modalOpen, activeWarning, timeUntilTimeout]);

  if (!isAuthenticated || !activeWarning) {
    return null;
  }

  const progressPercentage =
    activeWarning.type === 'modal'
      ? Math.max(0, (countdown / (2 * 60 * 1000)) * 100) // 2 minutes max for modal
      : 100;

  return (
    <>
      {/* Snackbar for subtle and prominent warnings */}
      <Snackbar
        open={snackbarOpen && activeWarning?.type !== 'modal'}
        onClose={(_, reason) => {
          if (reason !== 'clickaway' && activeWarning) {
            handleDismissSnackbar(activeWarning.id);
          }
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={activeWarning?.type === 'subtle' ? 8000 : null} // Auto-hide subtle, keep prominent
      >
        <Alert
          severity={activeWarning?.severity ?? 'info'}
          sx={{ width: '100%' }}
          action={
            activeWarning?.type === 'prominent' ? (
              <Button
                color="inherit"
                size="small"
                onClick={handleExtendSession}
                variant="outlined"
                sx={{ ml: 1 }}
              >
                Extend Session
              </Button>
            ) : undefined
          }
          onClose={() => activeWarning && handleDismissSnackbar(activeWarning.id)}
        >
          {activeWarning ? getWarningMessage(activeWarning, timeUntilTimeout) : ''}
        </Alert>
      </Snackbar>

      {/* Modal for critical warnings */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        disableEscapeKeyDown={timeUntilTimeout <= 30000} // Prevent escape when critical
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            border: '2px solid',
            borderColor: 'error.main',
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: 'error.light', color: 'error.contrastText' }}>
          ⚠️ Session Expiring Soon
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" gutterBottom>
            {activeWarning ? getWarningMessage(activeWarning, timeUntilTimeout) : ''}
          </Typography>

          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Time remaining:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progressPercentage}
                sx={{
                  flexGrow: 1,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'error.light',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: progressPercentage > 50 ? 'warning.main' : 'error.main',
                  },
                }}
              />
              <Typography
                variant="h6"
                color="error.main"
                sx={{ minWidth: '80px', textAlign: 'right' }}
              >
                {formatTime(timeUntilTimeout)}
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {timeoutType === 'idle'
              ? 'Your session will expire due to inactivity. Click any button or interact with the page to extend your session.'
              : 'Your session has reached the maximum duration limit. You will need to log in again.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          {timeoutType === 'idle' && (
            <Button
              onClick={handleExtendSession}
              variant="contained"
              color="primary"
              size="large"
              fullWidth
            >
              Extend Session
            </Button>
          )}
          {timeUntilTimeout > 30000 && (
            <Button onClick={handleCloseModal} variant="outlined" color="secondary">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProgressiveSessionWarnings;
